/* ==========================================================================
   Home page (portada). Vanilla JS, no dependencies (no jQuery, no Slick).
   The product tabs use jQuery only if present, to rerun the theme's
   plugins on the product cards (like collection-filters.js).
   Loaded with defer only on the home page from layout/theme.liquid.
   Each section initializes every [data-*] root it finds, and again on
   shopify:section:load when the theme editor re-renders it.
   ========================================================================== */
(function () {
  'use strict';

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ------------------------------------------------------------------------
     Fading slides: Portada grande (sections/home-hero.liquid) and
     Testimonios (sections/home-testimonials.liquid, below).
     One slide at a time, with dots. Autoplay pauses while the mouse is over
     the root, while keyboard focus is inside it, and while a slide is
     selected in the theme editor; it never runs with "reduce motion". With
     autoplay on, a pause button stops it until pressed again.
     ------------------------------------------------------------------------ */
  var HERO = '[data-home-hero]';
  var HERO_PARTS = { slide: '[data-hero-slide]', dot: '[data-hero-dot]', pause: '[data-hero-pause]' };
  var ACTIVE = 'is-active';

  // A mouse click on a dot also moves focus there; only keyboard focus pauses
  function keyboardFocus(el) {
    try {
      return el.matches(':focus-visible');
    } catch (err) {
      return true;
    }
  }

  function Fader(root, parts) {
    this.root = root;
    this.slides = root.querySelectorAll(parts.slide);
    this.dots = root.querySelectorAll(parts.dot);
    this.pauseBtn = parts.pause ? root.querySelector(parts.pause) : null;
    this.index = 0;
    this.timer = null;
    this.autoplay = root.getAttribute('data-autoplay') === 'true';
    this.delay = (parseInt(root.getAttribute('data-speed'), 10) || 6) * 1000;
    // Reasons to pause; autoplay runs only when all are false
    this.hovered = false;
    this.focused = false;
    this.selected = false;
    this.stopped = false; // pause button

    var self = this;
    this.onMotionChange = function () {
      self.updatePauseButton();
      self.schedule();
    };

    for (var i = 0; i < this.dots.length; i++) {
      this.dots[i].addEventListener('click', this.goTo.bind(this, i));
    }
    if (this.pauseBtn) {
      this.pauseBtn.addEventListener('click', this.toggleStop.bind(this));
    }
    // Pointer events, not mouse events: a tap fires mouseenter but never
    // mouseleave, which would leave autoplay paused for good on touch screens
    root.addEventListener('pointerenter', function (e) {
      if (e.pointerType !== 'mouse') return;
      self.hovered = true;
      self.schedule();
    });
    root.addEventListener('pointerleave', function (e) {
      if (e.pointerType !== 'mouse') return;
      self.hovered = false;
      self.schedule();
    });
    root.addEventListener('focusin', function (e) {
      if (!keyboardFocus(e.target)) return;
      self.focused = true;
      self.schedule();
    });
    root.addEventListener('focusout', function (e) {
      if (e.relatedTarget && root.contains(e.relatedTarget)) return;
      self.focused = false;
      self.schedule();
    });
    if (reducedMotion.addEventListener) {
      reducedMotion.addEventListener('change', this.onMotionChange);
    } else if (reducedMotion.addListener) {
      reducedMotion.addListener(this.onMotionChange);
    }

    this.updatePauseButton();
    this.schedule();
  }

  Fader.prototype.show = function (index) {
    var count = this.slides.length;
    this.index = (index + count) % count;
    for (var i = 0; i < count; i++) {
      var on = i === this.index;
      this.slides[i].classList.toggle(ACTIVE, on);
      if (this.dots[i]) {
        this.dots[i].classList.toggle(ACTIVE, on);
        if (on) {
          this.dots[i].setAttribute('aria-current', 'true');
        } else {
          this.dots[i].removeAttribute('aria-current');
        }
      }
    }
  };

  // Dot click: show that slide and restart the countdown
  Fader.prototype.goTo = function (index) {
    this.show(index);
    this.schedule();
  };

  // (Re)start or stop the autoplay countdown depending on the pause reasons
  Fader.prototype.schedule = function () {
    clearTimeout(this.timer);
    this.timer = null;
    if (!this.autoplay || this.slides.length < 2 || reducedMotion.matches) return;
    if (this.stopped || this.hovered || this.focused || this.selected) return;
    var self = this;
    this.timer = setTimeout(function () {
      self.show(self.index + 1);
      self.schedule();
    }, this.delay);
  };

  // Pause button. Resuming is explicit, so it plays right away even with the
  // mouse or keyboard focus still on the card (they pause again on re-entry).
  Fader.prototype.toggleStop = function () {
    this.stopped = !this.stopped;
    if (!this.stopped) {
      this.hovered = false;
      this.focused = false;
    }
    this.updatePauseButton();
    this.schedule();
  };

  // Icon and label follow the state; with "reduce motion" nothing moves, so
  // the button is hidden
  Fader.prototype.updatePauseButton = function () {
    var btn = this.pauseBtn;
    if (!btn) return;
    btn.hidden = reducedMotion.matches;
    btn.classList.toggle('is-paused', this.stopped);
    btn.setAttribute('aria-label', btn.getAttribute(this.stopped ? 'data-label-play' : 'data-label-pause'));
  };

  // Theme editor: show the selected slide and hold it there
  Fader.prototype.select = function (slide) {
    for (var i = 0; i < this.slides.length; i++) {
      if (this.slides[i] === slide) this.show(i);
    }
    this.selected = true;
    this.schedule();
  };

  Fader.prototype.deselect = function () {
    this.selected = false;
    this.schedule();
  };

  Fader.prototype.destroy = function () {
    clearTimeout(this.timer);
    if (reducedMotion.removeEventListener) {
      reducedMotion.removeEventListener('change', this.onMotionChange);
    } else if (reducedMotion.removeListener) {
      reducedMotion.removeListener(this.onMotionChange);
    }
  };

  /* ------------------------------------------------------------------------
     Testimonios (sections/home-testimonials.liquid)
     A Fader (same autoplay and pause rules as the hero, and the same pause
     button, which Liquid prints only with autoplay on) plus the arrows, the
     side avatars (each goes to its testimonial) and a horizontal swipe on
     touch screens. Changes made by the visitor are announced in the
     aria-live region; autoplay changes are not, so a screen reader isn't
     interrupted every few seconds.
     ------------------------------------------------------------------------ */
  var TESTI = '[data-home-testimonials]';
  var TESTI_PARTS = { slide: '[data-testi-slide]', dot: '[data-testi-dot]', pause: '[data-testi-pause]' };
  var SWIPE = 40; // minimum horizontal distance, in px

  function Testimonials(root) {
    Fader.call(this, root, TESTI_PARTS);
    this.live = root.querySelector('[data-testi-live]');

    var self = this;
    var prev = root.querySelector('[data-testi-prev]');
    var next = root.querySelector('[data-testi-next]');
    if (prev) prev.addEventListener('click', function () { self.goTo(self.index - 1); });
    if (next) next.addEventListener('click', function () { self.goTo(self.index + 1); });

    // Side avatars (every slide has its own)
    root.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('[data-testi-go]');
      if (btn) self.goFromAvatar(btn);
    });

    // Swipe: only clearly horizontal moves; vertical ones scroll the page
    // (touch-action: pan-y on the track)
    var track = root.querySelector('[data-testi-track]');
    var startX = null;
    var startY = 0;
    track.addEventListener('touchstart', function (e) {
      if (e.touches.length !== 1) {
        startX = null;
        return;
      }
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    }, { passive: true });
    track.addEventListener('touchend', function (e) {
      if (startX === null || self.slides.length < 2) return;
      var dx = e.changedTouches[0].clientX - startX;
      var dy = e.changedTouches[0].clientY - startY;
      startX = null;
      if (Math.abs(dx) < SWIPE || Math.abs(dx) < Math.abs(dy) * 1.5) return;
      self.goTo(self.index + (dx < 0 ? 1 : -1));
    }, { passive: true });
    track.addEventListener('touchcancel', function () {
      startX = null;
    }, { passive: true });
  }

  Testimonials.prototype = Object.create(Fader.prototype);
  Testimonials.prototype.constructor = Testimonials;

  // The visitor's changes (arrows, dots, avatars, swipe): show and announce
  Testimonials.prototype.goTo = function (index) {
    Fader.prototype.goTo.call(this, index);
    this.announce();
  };

  // The pressed avatar fades out with its slide, so keyboard focus moves to
  // the same side's avatar in the new one (Enter again keeps going that way)
  Testimonials.prototype.goFromAvatar = function (btn) {
    var hadFocus = document.activeElement === btn;
    var side = btn.classList.contains('home-testi__avatar--prev') ? 'prev' : 'next';
    this.goTo(parseInt(btn.getAttribute('data-testi-go'), 10) || 0);
    if (!hadFocus) return;
    var slide = this.slides[this.index];
    var target = slide.querySelector('.home-testi__avatar--' + side) || slide.querySelector('[data-testi-go]');
    if (target) target.focus();
  };

  Testimonials.prototype.announce = function () {
    var slide = this.slides[this.index];
    if (!this.live || !slide) return;
    var quote = slide.querySelector('[data-testi-quote]');
    var name = slide.querySelector('[data-testi-name]');
    var text = 'Testimonio ' + (this.index + 1) + ' de ' + this.slides.length + '.';
    if (quote) text += ' ' + quote.textContent.trim();
    if (name) text += ' ' + name.textContent.trim();
    this.live.textContent = text;
  };

  // Both kinds keep their instance on the root as homeFader
  var FADER = HERO + ', ' + TESTI;

  function initFaders(scope) {
    var i;
    var heroes = scope.querySelectorAll(HERO);
    for (i = 0; i < heroes.length; i++) {
      if (!heroes[i].homeFader) heroes[i].homeFader = new Fader(heroes[i], HERO_PARTS);
    }
    var testimonials = scope.querySelectorAll(TESTI);
    for (i = 0; i < testimonials.length; i++) {
      if (!testimonials[i].homeFader) testimonials[i].homeFader = new Testimonials(testimonials[i]);
    }
  }

  function destroyFaders(scope) {
    var roots = scope.querySelectorAll(FADER);
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].homeFader) {
        roots[i].homeFader.destroy();
        roots[i].homeFader = null;
      }
    }
  }

  // The hero or testimonials that own a block element (the slide itself, in
  // the editor)
  function faderFor(el) {
    var root = el.closest && el.closest(FADER);
    return root && root.homeFader;
  }

  /* ------------------------------------------------------------------------
     Paged carousel (.home-pager, mobile only, sections/home-categories.liquid)
     The track is a native horizontal scroll with CSS snap points on the
     first item of each page, so the finger moves one page (2 items) at a
     time, like the product page thumbnail strip. This only keeps the dots
     in sync and makes them clickable. On desktop the track doesn't scroll
     and the dots are hidden, so nothing happens.
     ------------------------------------------------------------------------ */
  var PAGER = '[data-home-pager]';

  function Pager(root) {
    this.root = root;
    this.track = root.querySelector('[data-pager-track]');
    this.dots = root.querySelectorAll('[data-pager-dot]');
    this.perPage = parseInt(root.getAttribute('data-per-page'), 10) || 2;
    this.ticking = false;

    var self = this;
    this.track.addEventListener('scroll', function () {
      if (self.ticking) return;
      self.ticking = true;
      window.requestAnimationFrame(function () {
        self.ticking = false;
        self.update();
      });
    }, { passive: true });
    for (var i = 0; i < this.dots.length; i++) {
      this.dots[i].addEventListener('click', this.goTo.bind(this, i));
    }
    this.update();
  }

  Pager.prototype.scrollable = function () {
    return this.track.scrollWidth > this.track.clientWidth + 1;
  };

  // A page is the track's width plus one gap (2 items + 2 gaps)
  Pager.prototype.pageWidth = function () {
    var gap = parseFloat(window.getComputedStyle(this.track).columnGap) || 0;
    return this.track.clientWidth + gap;
  };

  Pager.prototype.current = function () {
    var t = this.track;
    var max = t.scrollWidth - t.clientWidth;
    if (max <= 0) return 0;
    // At the end (an odd last page can't scroll a full page) → last dot
    if (t.scrollLeft >= max - 2) return Math.max(this.dots.length - 1, 0);
    return Math.round(t.scrollLeft / this.pageWidth());
  };

  Pager.prototype.update = function () {
    var page = this.current();
    for (var i = 0; i < this.dots.length; i++) {
      var on = i === page;
      this.dots[i].classList.toggle(ACTIVE, on);
      if (on) {
        this.dots[i].setAttribute('aria-current', 'true');
      } else {
        this.dots[i].removeAttribute('aria-current');
      }
    }
  };

  Pager.prototype.goTo = function (page) {
    this.track.scrollTo({
      left: page * this.pageWidth(),
      behavior: reducedMotion.matches ? 'auto' : 'smooth'
    });
  };

  // Theme editor: show the page that holds this item
  Pager.prototype.showItem = function (item) {
    var items = this.track.children;
    for (var i = 0; i < items.length; i++) {
      if (items[i] === item) this.goTo(Math.floor(i / this.perPage));
    }
  };

  function initPagers(scope) {
    var roots = scope.querySelectorAll(PAGER);
    for (var i = 0; i < roots.length; i++) {
      if (!roots[i].homePager) roots[i].homePager = new Pager(roots[i]);
    }
  }

  /* ------------------------------------------------------------------------
     Productos en pestañas (sections/home-products.liquid)
     Liquid renders every tab's grid; this only switches them, right away:
     click, ← / → (wrapping around), Inicio / Fin. Only the selected tab is
     in the tab order. The cards are the collection page's
     (product-grid-item): the theme sets them up on page load (tooltips,
     color swatches, wishlist hearts); what it doesn't do here is below.
     ------------------------------------------------------------------------ */
  var TABS = '[data-home-tabs]';

  function Tabs(root) {
    this.root = root;
    this.tabs = root.querySelectorAll('[data-tab]');
    this.panels = root.querySelectorAll('[data-tab-panel]');

    var self = this;
    Array.prototype.forEach.call(this.tabs, function (tab, i) {
      tab.addEventListener('click', function () { self.show(i); });
      tab.addEventListener('keydown', function (e) { self.onKey(e, i); });
    });
  }

  Tabs.prototype.show = function (index, focus) {
    for (var i = 0; i < this.panels.length; i++) {
      var on = i === index;
      this.panels[i].hidden = !on;
      var tab = this.tabs[i];
      if (!tab) continue;
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
      if (on) {
        tab.removeAttribute('tabindex');
        if (focus) tab.focus();
      } else {
        tab.setAttribute('tabindex', '-1');
      }
    }
  };

  Tabs.prototype.onKey = function (e, i) {
    var last = this.tabs.length - 1;
    var next = null;
    if (e.key === 'ArrowRight') next = i === last ? 0 : i + 1;
    else if (e.key === 'ArrowLeft') next = i === 0 ? last : i - 1;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = last;
    if (next === null) return;
    e.preventDefault();
    this.show(next, true);
  };

  // Theme editor: show the selected block's tab (the block is the panel)
  Tabs.prototype.showPanel = function (panel) {
    for (var i = 0; i < this.panels.length; i++) {
      if (this.panels[i] === panel) this.show(i);
    }
  };

  // Hearts of the card grid without the tooltip: on touch screens the
  // "Lista de Deseados" bubble stayed stuck after a tap. The title becomes
  // the button's aria-label (it has no visible text). Run before the theme
  // (theme.js, on document ready) turns tooltips on: this file is deferred,
  // so it runs first.
  // Copy of plainHearts() in assets/collection-filters.js: change both
  // together.
  function plainHearts(scope) {
    var $ = window.jQuery;
    scope.querySelectorAll('.cc-grid .button-group .wishlist[data-toggle="tooltip"]').forEach(function (heart) {
      if ($ && $.fn.tooltip) $(heart).tooltip('dispose');
      var label = heart.getAttribute('title') || heart.getAttribute('data-original-title');
      if (label) heart.setAttribute('aria-label', label);
      heart.removeAttribute('title');
      heart.removeAttribute('data-original-title');
      heart.removeAttribute('data-toggle');
    });
  }

  // On page load: hearts without tooltip, and the theme's lazy loading
  // plugin (vendor.js) as on the collection page (lazysizes also loads them)
  function prepareCards(root) {
    var $ = window.jQuery;
    plainHearts(root);
    if ($ && $.fn.lazyload) $(root).find('img.lazyload').lazyload();
  }

  // After the theme editor re-renders the section, also what the theme only
  // sets up on page load. Same as activate() in collection-filters.js.
  function activateCards(root) {
    var $ = window.jQuery;
    prepareCards(root);
    // Quick view and compare tooltips (theme.js)
    if ($ && $.fn.tooltip) $(root).find('[data-toggle="tooltip"]').tooltip();
    // Hearts of products already in the wishlist (wishlist.js; clicks are delegated)
    if (window.Wishlist && window.Wishlist.activate) window.Wishlist.activate(root);
    // Color swatches swap the card image (theme.js)
    root.querySelectorAll('.product-color li label').forEach(function (label) {
      label.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var link = label.parentNode.querySelector('.hidden a');
        var card = label.closest('.product-wrapper');
        if (!link || !card) return;
        card.querySelectorAll('.popup_cart_image').forEach(function (img) {
          img.setAttribute('src', link.getAttribute('href'));
        });
      });
    });
  }

  function initTabs(scope, reloaded) {
    var roots = scope.querySelectorAll(TABS);
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].homeTabs) continue;
      roots[i].homeTabs = new Tabs(roots[i]);
      if (reloaded) {
        activateCards(roots[i]);
      } else {
        prepareCards(roots[i]);
      }
    }
  }

  /* Cart popup image. Shopify.cartPopap (cart.api.js) takes the image of the
     first card on the page with the variant's class (".<variant id>
     .popup_cart_image"). The same product can be in several tabs, and a
     card in a tab that was never shown has no src yet (lazy images only
     have data-src), so the popup came out without a photo. On a cart button
     click in these sections, every card of that variant on the page gets
     its src first. Capture phase, so it runs before the button's onclick
     (Shopify.addItem); the popup opens only after the Ajax response. */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest(TABS + ' .cart-hover .btn-cart');
    var card = btn && btn.closest('.product-item');
    if (!card) return;
    var id = null;
    for (var i = 0; i < card.classList.length; i++) {
      if (/^\d+$/.test(card.classList[i])) id = card.classList[i];
    }
    if (!id) return;
    document.querySelectorAll('[class~="' + id + '"] .popup_cart_image').forEach(function (img) {
      var src = img.getAttribute('data-src');
      if (src && !img.getAttribute('src')) img.setAttribute('src', src);
    });
  }, true);

  /* ------------------------------------------------------------------------
     Init and theme editor events
     ------------------------------------------------------------------------ */
  function init(scope, reloaded) {
    initFaders(scope || document);
    initPagers(scope || document);
    initTabs(scope || document, reloaded);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (e) {
    init(e.target, true);
  });

  // Theme editor: show the tab of the selected block
  document.addEventListener('shopify:block:select', function (e) {
    var root = e.target.closest && e.target.closest(TABS);
    var tabs = root && root.homeTabs;
    if (tabs) tabs.showPanel(e.target);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    destroyFaders(e.target);
  });

  // Theme editor: show the selected slide or testimonial and hold it there
  document.addEventListener('shopify:block:select', function (e) {
    var fader = faderFor(e.target);
    if (fader) fader.select(e.target);
  });

  document.addEventListener('shopify:block:deselect', function (e) {
    var fader = faderFor(e.target);
    if (fader) fader.deselect();
  });

  /* Mobile carousels (.home-pager): the editor scrolls the page to the
     selected block but not the carousel, so show the page that holds it.
     On desktop the track doesn't scroll, so this does nothing. */
  document.addEventListener('shopify:block:select', function (e) {
    var root = e.target.closest && e.target.closest(PAGER);
    var pager = root && root.homePager;
    if (pager && pager.scrollable()) pager.showItem(e.target);
  });
})();
